/** @prettier */

import {isUndefined} from 'lodash';
import React, {useContext, useEffect, useState} from 'react';
import {
  Grid,
  Box,
  Stack,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {utcToZonedTime, zonedTimeToUtc} from 'date-fns-tz';
import {format} from 'date-fns';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import {ContextApplyFilterTab} from './MySiteArea';
import {BarGraphButton, LineGraphButton} from './SiteSubContentsIconButton';
//API import
import {
  getStatesSitesRt,
  getCompositionsGateway,
  getCompositionsGroups,
  getCompositionsRTUnit,
  getSettingUser,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//type import
import {GraphFormType, GraphTargetType, mysite} from '../../Type/TypeDef';
//Style import
import '../../css/common/common.css';

import {
  invertComma,
  numberWithPeriod,
  roundOff,
} from '../../functions/convertString';
import {iconBlack, iconWhite, iconYellow, iconRed} from '../../define';

//空調機一覧の幅一覧
//変更した場合、ホバーした際のボタンのレイアウトを確認すること
const gridXsAirUnit = 3.5;
const gridXsAirState = 4;
const gridXsAirKwh = 4.5;

/**
 * マイサイト操作画面（その他）
 * @param props props
 * @returns JSX
 */
const ViewOther = (props: {
  siteProfile: apiIF.resParameters.siteInformations;
  selectedItem: string;
  setSelectedItem: React.Dispatch<React.SetStateAction<string>>;
  setGraphParam: (
    graphTarget: GraphTargetType,
    graphTargetID?: string,
    graphType?: GraphFormType,
    graphTitle?: string,
    tabType?: 'bar' | 'line' | 'diagram',
    childUnitInfo?: apiIF.resParameters.rtUnitInformations[]
  ) => void;
  otherFilter: mysite.filterProps['unitType'];
}) => {
  const trans = useTranslation().t; //翻訳関数

  const siteID = props.siteProfile['site-id'];
  const timeZone = props.siteProfile['timezone'];

  //prettier-ignore
  //Q-ton機器の状態値
  const [qtonData, setQtonData] = useState<apiIF.resParameters.rtOUStates[]>([]);
  //prettier-ignore
  //Hyozan室外機の状態値
  const [hyozanOUData, setHyozanOUData] = useState<apiIF.resParameters.rtOUStates[]>([]);
  //prettier-ignore
  //Hyozan室内機の状態値
  const [hyozanIUData, setHyozanIUData] = useState<apiIF.resParameters.rtIUStates[]>([]);
  //prettier-ignore
  //Modbus室外機の状態値
  const [modbusOUData, setModbusOUData] = useState<apiIF.resParameters.modbusOUStates[]>([]);
  //prettier-ignore
  //Modbus室内機の状態値
  const [modbusIUData, setModbusIUData] = useState<apiIF.resParameters.modbusIUStates[]>([]);
  //prettier-ignore
  //Chiller機器の状態値
  const [chillerData, setChillerData] = useState<apiIF.resParameters.rtOUStates[]>([]);
  //HCCVQグループの情報
  const [hccvqGroupData, setHCCVQGroupData] = useState<{
    hyozan: apiIF.resParameters.groupInformations[];
    modbus: apiIF.resParameters.groupInformations[];
  }>({hyozan: [], modbus: []});
  //構成情報
  const [compositionsData, setCompositionsData] = useState<{
    rt: {ouID: string; iuIDList: string[]}[];
    modbus: {ouID: string; iuIDList: string[]}[];
  }>({rt: [], modbus: []});
  //prettier-ignore
  //フィルタ状態
  const [unitTypeFilter, setUnitTypeFilter] = useState<mysite.filterProps['unitType']>('');
  //prettier-ignore
  //親機が登録されているQ-ton機器の名前とユニットIDと親機のID一覧
  const [childUnitInfo, setChildUnitInfo] = useState<apiIF.resParameters.rtUnitInformations[]>([]);
  //ホバーしている空調機
  const [hoveringUnit, setHoveringUnit] = useState('');
  //prettier-ignore
  //圧力の単位
  const [pressureUnit, setPressureUnit] = useState<apiIF.res.getSettingUser['unit']['pressure']>('MPa');

  useEffect(() => {
    setUnitTypeFilter(props.otherFilter);
  }, [props.otherFilter]);

  useEffect(() => {
    //タイムゾーンがundefinedの場合は処理しない
    if (isUndefined(timeZone)) return;

    /** タイムゾーンを考慮した現在時刻 */
    const nowDate = utcToZonedTime(new Date(), timeZone);
    //prettier-ignore
    //開始を午前0時とする
    const start = format(nowDate, 'yyyy-MM-dd 00:00:00' );
    const afterDate = zonedTimeToUtc(start, timeZone).toISOString();
    //prettier-ignore
    //終了を現在時刻とする
    const now = format(nowDate, 'yyyy-MM-dd HH:mm:ss');
    const beforeDate = zonedTimeToUtc(now, timeZone).toISOString();

    //RT機器の状態値を取得
    const reqStatesRt = () => {
      const rtPromises = [];
      //RT機器の状態値を取得
      rtPromises.push(
        getStatesSitesRt(siteID, {
          fields:
            //全機種共通
            'unit-name,unit-type,run-stop,error-code,' +
            //Q-ton
            'outlet-temperature,hot-water-amount,remote-control-preset-temperature,' +
            //Hyozan室外機
            'high-pressure,low-pressure,' +
            //Hyozan室内機
            'indoor-unit-name,preset-temperature,inside-temperature,difference-level,connection-status,' +
            //Chiller
            'outlet-water-temperature,operation-mode',
        })
      );
      //RT機器の当日分の消費電力、熱量を取得
      //消費電力、熱量は0時から現在時刻までのデータを集計するため、通常の状態値とは別にAPIの呼び出しが必要
      rtPromises.push(
        getStatesSitesRt(siteID, {
          'fields': 'power-consumption,energy-amount',
          'after-time': afterDate,
          'before-time': beforeDate,
          'every-minute': 1440,
        })
      );
      Promise.all(rtPromises)
        .then(statesResponse => {
          /** 室外機の状態値 */
          const ouStates =
            statesResponse[0]['rt-states'][0]['outdoor-unit-states'];
          /** 室内機の状態値 */
          const iuStates =
            statesResponse[0]['rt-states'][0]['indoor-unit-states'];
          /** 消費電力 */
          const power =
            statesResponse[1]['rt-states'][0]['outdoor-unit-states'];

          //室外機
          const {qton, hyozan, chiller} = ouStates.reduce(
            (result, state) => {
              //unitIDで検索し、消費電力を抽出
              const searchResult = power.find(info => {
                return info['unit-id'] === state['unit-id'];
              });

              switch (state['unit-type']) {
                case 'QtonOpen': //fall through
                case 'QtonClose':
                  if (
                    searchResult !== undefined &&
                    searchResult['power-consumption'] !== undefined
                  ) {
                    result.qton.push({
                      ...state,
                      'power-consumption': {
                        'cooling-mode': '',
                        'heating-mode':
                          searchResult['power-consumption']['heating-mode'],
                      },
                      'energy-amount': searchResult['energy-amount'],
                    });
                  } else {
                    result.qton.push(state);
                  }
                  break;
                case 'Chiller':
                  if (
                    searchResult !== undefined &&
                    searchResult['power-consumption'] !== undefined
                  ) {
                    result.chiller.push({
                      ...state,
                      'power-consumption': {
                        'cooling-mode':
                          searchResult['power-consumption']['cooling-mode'],
                        'heating-mode':
                          searchResult['power-consumption']['heating-mode'],
                      },
                    });
                  } else {
                    result.chiller.push(state);
                  }
                  break;
                case 'Hyozan':
                  if (
                    searchResult !== undefined &&
                    searchResult['power-consumption'] !== undefined
                  ) {
                    result.hyozan.push({
                      ...state,
                      'power-consumption': {
                        'cooling-mode':
                          searchResult['power-consumption']['cooling-mode'],
                        'heating-mode': '',
                      },
                    });
                  } else {
                    result.hyozan.push(state);
                  }
                  break;
              }
              return result;
            },
            {
              qton: [] as apiIF.resParameters.rtOUStates[],
              hyozan: [] as apiIF.resParameters.rtOUStates[],
              chiller: [] as apiIF.resParameters.rtOUStates[],
            }
          );
          setQtonData(qton);
          setChillerData(chiller);
          setHyozanOUData(hyozan);
          //室内機（Hyozanのみ）
          setHyozanIUData(iuStates);
        })
        .catch(err => {
          setQtonData([]);
          setChillerData([]);
          setHyozanOUData([]);
          setHyozanIUData([]);
          setChillerData([]);
        });
    };

    //グループの情報を取得
    getCompositionsGroups(siteID, {
      'fields': 'group-name,unit-system,unit-informations',
      'max-result': 400,
    }).then(response => {
      const {hyozan, modbus} = response['group-informations'].reduce(
        (result, info) => {
          if (info['unit-system'] === 'RT') {
            result.hyozan.push(info);
          } else if (info['unit-system'] === 'Modbus') {
            result.modbus.push(info);
          }
          return result;
        },
        {hyozan: [], modbus: []} as {
          hyozan: apiIF.resParameters.groupInformations[];
          modbus: apiIF.resParameters.groupInformations[];
        }
      );
      setHCCVQGroupData({hyozan: hyozan, modbus: modbus});
    });
    //HCCVQの構成情報を取得
    getCompositionsGateway(siteID, {
      'fields': 'compositions',
      'max-result': 200,
    })
      .then(response => {
        const {rt, modbus} = response['gateway-informations'].reduce(
          (result, gwInfo) => {
            if (gwInfo.compositions === undefined) return result;
            gwInfo.compositions['modbus-system'].forEach(ouInfo => {
              result.modbus.push({
                ouID: ouInfo['outdoor-id'],
                iuIDList: ouInfo['indoor-unit'].map(iuInfo => {
                  return iuInfo['unit-id'];
                }),
              });
            });

            gwInfo.compositions['rt-system-1'].forEach(ouInfo => {
              //hyozan以外の空調機も入力されるがグループ情報のデータから空調機を検索するため問題ない
              result.rt.push({
                ouID: ouInfo['unit-id'],
                iuIDList: ouInfo['indoor-unit'].map(iuInfo => {
                  return iuInfo['unit-id'];
                }),
              });
            });
            gwInfo.compositions['rt-system-2'].forEach(ouInfo => {
              //hyozan以外の空調機も入力されるがグループ情報のデータから空調機を検索するため問題ない
              result.rt.push({
                ouID: ouInfo['unit-id'],
                iuIDList: ouInfo['indoor-unit'].map(iuInfo => {
                  return iuInfo['unit-id'];
                }),
              });
            });
            return result;
          },
          {rt: [], modbus: []} as {
            rt: {ouID: string; iuIDList: string[]}[];
            modbus: {ouID: string; iuIDList: string[]}[];
          }
        );
        setCompositionsData({rt: rt, modbus: modbus});
        //GW毎のRT機器の親子設定情報を取得
        const rtNumPromises = response['gateway-informations'].map(
          async gwInfo => {
            try {
              //熱源機の情報を取得
              const query = {
                'fields': 'unit-name,parent-unit',
                'max-result': 400,
              };
              const rtInfo = await getCompositionsRTUnit(
                siteID,
                gwInfo['gateway-id'],
                query
              );
              //Q-ton機のunitIDと親機設定保存配列
              return rtInfo['unit-informations'].filter(unitInfo => {
                return unitInfo['parent-unit'] !== '';
              });
            } catch (error) {
              throw error;
            }
          }
        );
        return Promise.all(rtNumPromises);
      })
      .then(result => {
        //GWごとに取得した情報をまとめる
        //親機設定がされている機器のユニット名と親機のIDと子機のIDの一覧の作成
        const resultArray: apiIF.resParameters.rtUnitInformations[] = [];
        result.forEach(rtInfo => {
          resultArray.push(...rtInfo);
        });
        setChildUnitInfo(resultArray);
      });
    getSettingUser().then(response => {
      //圧力単位を取得
      setPressureUnit(response['unit'].pressure);
    });

    //ページ遷移時初回読み込み
    reqStatesRt();
    //定期実行
    const intervalId = setInterval(() => {
      reqStatesRt();
    }, 60000);

    return function () {
      clearInterval(intervalId);
    };
  }, [siteID, timeZone]);

  /**
   * 対象のQ-ton機が子機であるかどうか判断する関数
   * @param unitID 対象のユニットID
   * @returns 子機であるかどうかの成否
   */
  const isChildUnit = (unitID: string) => {
    let result = childUnitInfo.some(info => info['unit-id'] === unitID);
    return result;
  };
  /**
   * Q-ton子機の場合に、親機のユニットIDを取得する関数
   * @param unitID 対象の子機のユニットID
   * @returns 親機のユニットID
   */
  const searchParentID = (unitID: string) => {
    let parentID = '';
    //子機の親機設定情報から親機ユニットのIDを検索する
    const targetInfo = childUnitInfo.find(info => info['unit-id'] === unitID);
    if (targetInfo !== undefined && targetInfo['parent-unit'] !== undefined) {
      parentID = targetInfo['parent-unit'];
    }
    //子機の親機設定情報から親機のIDを取得する
    return parentID;
  };

  return (
    <>
      {/** フィルタ情報 */}
      <Box className="subContents__content__filterChip">
        <FilterButton
          unitTypeFilter={unitTypeFilter}
          setUnitTypeFilter={setUnitTypeFilter}
        />
      </Box>
      {/** ヘッダ */}
      <Grid
        container
        alignItems="center"
        className="subContents__content__header">
        <Grid
          item
          xs={gridXsAirUnit}
          className="subContents__content__header__category">
          {trans('ViewQtonHyozan.header.unit')}
        </Grid>
        <Grid
          item
          xs={gridXsAirState}
          className="subContents__content__header__status">
          {trans('ViewQtonHyozan.header.status')}
        </Grid>
        <Grid
          item
          xs={gridXsAirKwh}
          className="subContents__content__header__power">
          {trans('ViewQtonHyozan.header.consumption')}
        </Grid>
      </Grid>
      {/** 状態値 */}
      <Box className="subContents__content__body">
        <Stack>
          {/** Chiller */}
          {(unitTypeFilter === '' || unitTypeFilter === 'chiller') &&
          chillerData.length > 0 ? (
            <>
              {/* Chillerのタイトルバー */}
              <Box
                onClick={() => setUnitTypeFilter('chiller')}
                className="subContents__content__body__data__unitType">
                {trans('ViewQtonHyozan.body.label.chiller')}
              </Box>
              {/* Chillerの状態値 */}
              {chillerData.map((state, index) => (
                <Box
                  key={`OtherViewChillerData${state['unit-id']}`}
                  className={
                    index === 0 ? '' : 'subContents__content__body--basic'
                  }>
                  <ChillerView
                    siteID={siteID}
                    state={state}
                    selectedUnit={props.selectedItem}
                    setSelectedUnit={props.setSelectedItem}
                    hoveringUnit={hoveringUnit}
                    setHoveringUnit={setHoveringUnit}
                    setUnitTypeFilter={setUnitTypeFilter}
                    setGraphParam={props.setGraphParam}
                  />
                </Box>
              ))}
            </>
          ) : (
            <></>
          )}
          {/** HCCV(Q) */}
          {(unitTypeFilter === '' || unitTypeFilter === 'hccvq') &&
          (hyozanOUData.length > 0 || modbusOUData.length > 0) ? (
            <>
              {/* HCCV(Q)のタイトルバー */}
              <Box
                onClick={() => setUnitTypeFilter('hccvq')}
                className="subContents__content__body__data__unitType">
                {trans('ViewQtonHyozan.body.label.hccvq')}
              </Box>
              {/* HCCV(Q)の状態値 */}
              {hyozanOUData.map((state, index) => {
                const iuIDList = compositionsData.rt.find(info => {
                  return info.ouID === state['unit-id'];
                });
                return (
                  <Box
                    key={`OtherViewHyozanData${state['unit-id']}`}
                    className={
                      index === 0 ? '' : 'subContents__content__body--basic'
                    }>
                    <HyozanOUView
                      siteID={siteID}
                      state={state}
                      selectedUnit={props.selectedItem}
                      setSelectedUnit={props.setSelectedItem}
                      hoveringUnit={hoveringUnit}
                      setHoveringUnit={setHoveringUnit}
                      pressureUnit={pressureUnit}
                      iuIDList={iuIDList === undefined ? [] : iuIDList.iuIDList}
                      setUnitTypeFilter={setUnitTypeFilter}
                      setGraphParam={props.setGraphParam}
                    />
                  </Box>
                );
              })}
              {modbusOUData.map((state, index) => {
                const iuIDList = compositionsData.modbus.find(info => {
                  return info.ouID === state['outdoor-id'];
                });
                return (
                  <Box
                    key={`OtherViewModbusData${state['outdoor-id']}`}
                    className={
                      index === 0 ? '' : 'subContents__content__body--basic'
                    }>
                    <ModbusOUView
                      siteID={siteID}
                      state={state}
                      selectedUnit={props.selectedItem}
                      setSelectedUnit={props.setSelectedItem}
                      hoveringUnit={hoveringUnit}
                      setHoveringUnit={setHoveringUnit}
                      pressureUnit={pressureUnit}
                      iuIDList={iuIDList === undefined ? [] : iuIDList.iuIDList}
                      setUnitTypeFilter={setUnitTypeFilter}
                      setGraphParam={props.setGraphParam}
                    />
                  </Box>
                );
              })}
              {hccvqGroupData.hyozan
                //ユニットが登録されているグループでフィルタする
                .filter(groupInfo => {
                  return (
                    groupInfo['unit-informations'] !== undefined &&
                    groupInfo['unit-informations'].length > 0
                  );
                })
                .map((groupInfo, groupIndex) => {
                  if (groupInfo['unit-informations'] === undefined) {
                    return (
                      <React.Fragment
                        key={`HyozanIUViewData${groupInfo['group-id']}`}></React.Fragment>
                    );
                  }
                  return (
                    <Grid
                      container
                      key={`HyozanIUViewData${groupInfo['group-id']}`}
                      className={
                        groupIndex === 0
                          ? ''
                          : 'subContents__content__body--basic'
                      }>
                      <Grid
                        item
                        xs={12}
                        className="subContents__content__body__data__group">
                        {groupInfo['group-name']}
                      </Grid>
                      {groupInfo['unit-informations'].map(unitInfo => {
                        const ouInfo = compositionsData.rt.find(info => {
                          return info.iuIDList.includes(unitInfo['unit-id']);
                        });
                        const unitState = hyozanIUData.find(data => {
                          return data['unit-id'] === unitInfo['unit-id'];
                        });
                        if (isUndefined(unitState)) {
                          return (
                            <Grid item xs={12} key={unitInfo['unit-id']}></Grid>
                          );
                        }
                        return (
                          <Grid item xs={12} key={unitInfo['unit-id']}>
                            <HCCVQIUView
                              state={unitState}
                              selectedUnit={props.selectedItem}
                              setSelectedUnit={props.setSelectedItem}
                              hoveringUnit={hoveringUnit}
                              setHoveringUnit={setHoveringUnit}
                              ouID={ouInfo === undefined ? '' : ouInfo.ouID}
                              destination={`/pages/my-site/site-detail/${siteID}/state/hyozan-group/${groupInfo['group-id']}`}
                              setUnitTypeFilter={setUnitTypeFilter}
                              graphTarget="hyozanIndoor"
                              setGraphParam={props.setGraphParam}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  );
                })}
              {hccvqGroupData.modbus
                //ユニットが登録されているグループでフィルタする
                .filter(groupInfo => {
                  return (
                    groupInfo['unit-informations'] !== undefined &&
                    groupInfo['unit-informations'].length > 0
                  );
                })
                .map((groupInfo, groupIndex) => {
                  if (groupInfo['unit-informations'] === undefined) {
                    return (
                      <React.Fragment
                        key={`ModbusIUViewData${groupInfo['group-id']}`}></React.Fragment>
                    );
                  }
                  return (
                    <Grid
                      container
                      key={`ModbusIUViewData${groupInfo['group-id']}`}
                      className={
                        groupIndex === 0
                          ? ''
                          : 'subContents__content__body--basic'
                      }>
                      <Grid
                        item
                        xs={12}
                        className="subContents__content__body__data__group">
                        {groupInfo['group-name']}
                      </Grid>
                      {groupInfo['unit-informations'].map(unitInfo => {
                        const ouInfo = compositionsData.modbus.find(info => {
                          return info.iuIDList.includes(unitInfo['unit-id']);
                        });
                        const unitState = modbusIUData.find(data => {
                          return data['unit-id'] === unitInfo['unit-id'];
                        });
                        if (isUndefined(unitState)) {
                          return (
                            <Grid item xs={12} key={unitInfo['unit-id']}></Grid>
                          );
                        }
                        return (
                          <Grid item xs={12} key={unitInfo['unit-id']}>
                            <HCCVQIUView
                              state={unitState}
                              selectedUnit={props.selectedItem}
                              setSelectedUnit={props.setSelectedItem}
                              hoveringUnit={hoveringUnit}
                              setHoveringUnit={setHoveringUnit}
                              ouID={ouInfo === undefined ? '' : ouInfo.ouID}
                              destination={`/pages/my-site/site-detail/${siteID}/state/modbus-group/${groupInfo['group-id']}`}
                              setUnitTypeFilter={setUnitTypeFilter}
                              graphTarget="modbusIndoor"
                              setGraphParam={props.setGraphParam}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  );
                })}
            </>
          ) : (
            <></>
          )}
          {/** Q-ton */}
          {(unitTypeFilter === '' || unitTypeFilter === 'qton') &&
          qtonData.length > 0 ? (
            <>
              {/* Q-tonのタイトルバー */}
              <Box
                onClick={() => setUnitTypeFilter('qton')}
                className="subContents__content__body__data__unitType">
                {trans('ViewQtonHyozan.body.label.qton')}
              </Box>
              {/* Q-tonの状態値 */}
              {qtonData.map((state, index) => {
                return (
                  <Box
                    key={`OtherViewQtonData${state['unit-id']}`}
                    className={
                      index === 0 ? '' : 'subContents__content__body--basic'
                    }>
                    {isChildUnit(state['unit-id']) ? (
                      <QtonChildView
                        siteID={siteID}
                        state={state}
                        selectedUnit={props.selectedItem}
                        setSelectedUnit={props.setSelectedItem}
                        hoveringUnit={hoveringUnit}
                        parentUnitID={searchParentID(state['unit-id'])}
                        childUnitInfo={childUnitInfo}
                        setHoveringUnit={setHoveringUnit}
                        setUnitTypeFilter={setUnitTypeFilter}
                        setGraphParam={props.setGraphParam}
                      />
                    ) : (
                      <QtonView
                        siteID={siteID}
                        state={state}
                        selectedUnit={props.selectedItem}
                        setSelectedUnit={props.setSelectedItem}
                        hoveringUnit={hoveringUnit}
                        setHoveringUnit={setHoveringUnit}
                        childUnitInfo={childUnitInfo}
                        setUnitTypeFilter={setUnitTypeFilter}
                        setGraphParam={props.setGraphParam}
                      />
                    )}
                  </Box>
                );
              })}
            </>
          ) : (
            <></>
          )}
        </Stack>
      </Box>
    </>
  );
};

/**
 * フィルタの適用情報の表示 押下でフィルタキャンセル
 * @param props props
 * - unitTypeFilter 適用している種類
 * - setUnitTypeFilter 適用している種類のset関数
 * @return JSX
 */
const FilterButton = (props: {
  unitTypeFilter: mysite.filterProps['unitType'];
  setUnitTypeFilter: React.Dispatch<
    React.SetStateAction<mysite.filterProps['unitType']>
  >;
}) => {
  const trans = useTranslation().t; //翻訳関数
  /** フィルタ、タブ状態をセットする関数 */
  const {applyFilter} = useContext(ContextApplyFilterTab);

  const unitTypeTable = (unitType: 'qton' | 'hccvq' | 'chiller') => {
    switch (unitType) {
      case 'qton':
        return trans('ViewQtonHyozan.buttonTxt.filter.qton');
      case 'hccvq':
        return trans('ViewQtonHyozan.buttonTxt.filter.hccvq');
      case 'chiller':
        return trans('ViewQtonHyozan.buttonTxt.filter.chiller');
    }
  };

  if (props.unitTypeFilter === '') return <></>;
  return (
    // フィルタの解除ボタン
    <Chip
      deleteIcon={
        <img
          src={iconRed.CLOSE.src}
          alt={`${trans(
            `ViewQtonHyozan.body.image.alternativeText.${iconRed.CLOSE.alt}`
          )}`}
          className="subContents__content__filterChip__icon"
        />
      }
      label={
        <Box
          title={unitTypeTable(props.unitTypeFilter)}
          className="subContents__content__filterChip__text">
          {unitTypeTable(props.unitTypeFilter)}
        </Box>
      }
      onDelete={() => {
        applyFilter({
          floor: '',
          groupID: '',
          otherUnitType: '',
        });
        props.setUnitTypeFilter('');
      }}
      variant="outlined"
      id="mv-unitsClearFilterBtn"
    />
  );
};
/**
 * Chiller
 * @param props props
 * - siteID サイトID
 * - state 状態値
 * - selectedUnit 選択状態のユニット
 * - setSelectedUnit 選択状態のユニットのset関数
 * - hoveringUnit　ホバー中のユニット
 * - setHoveringUnit ホバー中ユニットのset関数
 * - setUnitTypeFilter フィルタのset関数
 * - setGraphParam グラフのパラメータのset関数
 * @returns JSX
 */
const ChillerView = (props: {
  siteID: string;
  state: apiIF.resParameters.rtOUStates;
  selectedUnit: string;
  setSelectedUnit: React.Dispatch<React.SetStateAction<string>>;
  hoveringUnit: string;
  setHoveringUnit: React.Dispatch<React.SetStateAction<string>>;
  setUnitTypeFilter: React.Dispatch<
    React.SetStateAction<mysite.filterProps['unitType']>
  >;
  setGraphParam: (
    graphTarget: GraphTargetType,
    graphTargetID?: string,
    graphType?: GraphFormType,
    graphTitle?: string,
    tabType?: 'bar' | 'line' | 'diagram'
  ) => void;
}) => {
  const trans = useTranslation().t; //翻訳関数
  const navigate = useNavigate();

  //prettier-ignore
  const [hoveringIcon, setHoveringIcon] = useState<'' | 'bar' | 'line'>(''); //ホバー状態のアイコン

  /** 選択されているかどうか */
  const isSelected = props.state['unit-id'] === props.selectedUnit;
  /** ホバーされているかどうか */
  const isHover = props.state['unit-id'] === props.hoveringUnit;
  /** エラーユニットフラグ エラー有:true エラー無:false */
  const errUnitFlg =
    props.state['error-code'] !== undefined &&
    props.state['error-code'] !== '' && //無課金状態
    props.state['error-code'] !== '00';

  /** 運転/停止アイコン */
  const runStopIconSrc = (
    runStopState: apiIF.resParameters.rtOUStates['run-stop']
  ) => {
    switch (runStopState) {
      case 'run':
        return iconWhite.FREEZER_ON.src;
      case 'stop':
        return iconBlack.FREEZER_OFF.src;
      default:
        return iconBlack.MISSING.src;
    }
  };
  /** 運転/停止アイコン用クラス名 */
  const runStopIconClassName = (
    runStopState: apiIF.resParameters.rtOUStates['run-stop']
  ) => {
    switch (runStopState) {
      case 'run':
        return 'subContents__content__body__data__status__runStopIcon--run';
      case 'stop':
        return 'subContents__content__body__data__status__runStopIcon--stop';
      default:
        return 'subContents__content__body__data__status__runStopIcon--stop';
    }
  };
  /**
   * 運転モード表示名変換
   *
   * @param modeState 運転モードの状態。空文字の場合は状態値なしとして--を表示。
   * @returns 運転モードに応じた表示名を返します。'cooling', 'heating'以外の運転モードの場合はNoneの表示を返します。
   */
  const operationModeName = (
    modeState: apiIF.resParameters.rtOUStates['operation-mode']
  ) => {
    switch (modeState) {
      case 'cooling':
        return trans('ViewQtonHyozan.body.parameter.value.mode.cooling');
      case 'heating':
        return trans('ViewQtonHyozan.body.parameter.value.mode.heating');
      case '':
        return '--';
      default:
        return trans('ViewQtonHyozan.body.parameter.value.mode.none');
    }
  };

  /**
   * アコーディオンの状態変更時
   * @param event
   * @param isExpanded
   */
  const handleChange = (event: React.SyntheticEvent, isExpanded: boolean) => {
    if (isExpanded) {
      props.setSelectedUnit(props.state['unit-id']);
      navigate(
        `/pages/my-site/site-detail/${props.siteID}/state/chiller/${props.state['unit-id']}`
      );
    } else {
      props.setSelectedUnit('');
    }
  };

  return (
    <Accordion
      disableGutters={true}
      expanded={isSelected}
      onChange={handleChange}
      onMouseEnter={() => props.setHoveringUnit(props.state['unit-id'])}
      onMouseLeave={() => props.setHoveringUnit('')}
      className={
        isSelected
          ? 'subContents__content__body__data--selected'
          : 'subContents__content__body__data'
      }>
      {/* アコーディオン閉じ状態 */}
      <AccordionSummary
        aria-controls={`${props.state['unit-name']}-content`}
        className="subContents__content__body__data__accordionSummary">
        <Grid
          container
          alignItems="center"
          onClick={() => {
            props.setGraphParam('');
          }}
          className="subContents__content__body__data__accordionSummary__accordionData">
          {/** 空調機名 */}
          <Grid
            item
            xs={gridXsAirUnit}
            title={props.state['unit-name']}
            onClick={() => {
              props.setUnitTypeFilter('chiller');
            }}
            className="subContents__content__body__data__category">
            {props.state['unit-name']}
          </Grid>
          {/** 運転/停止、異常表示 */}
          <Grid
            item
            xs={gridXsAirState}
            container
            columnSpacing={2}
            alignItems="center">
            {/** 運転/停止 */}
            <Grid item xs="auto">
              <Box className={runStopIconClassName(props.state['run-stop'])}>
                <img
                  src={runStopIconSrc(props.state['run-stop'])}
                  className="subContents__content__body__data__status__runStopIcon__image"
                  alt={`${trans(
                    'ViewQtonHyozan.body.image.alternativeText.runStopIcon'
                  )}`}
                />
              </Box>
            </Grid>
            {/** 異常表示 */}
            {/** 異常時のみ表示する */}
            {errUnitFlg && (
              <Grid item xs="auto">
                <img
                  src={
                    isHover && hoveringIcon === ''
                      ? iconWhite.ERROR.src //空調機をホバー かつ グラフアイコンをホバー
                      : iconYellow.ERROR.src
                  }
                  alt={`${trans(
                    'ViewQtonHyozan.body.image.alternativeText.errIcon'
                  )}`}
                  className="subContents__content__body__data__status__errorIconForUnit"
                />
              </Grid>
            )}
          </Grid>
          {/** 消費電力量、グラフ */}
          <Grid
            item
            xs={gridXsAirKwh}
            container
            justifyContent="flex-end"
            className="subContents__content__body__data__powerGraphOpe">
            {/** 消費電力量 */}
            <Grid
              item
              xs="auto"
              className="subContents__content__body__data__powerGraphOpe__power">
              {props.state['power-consumption'] !== undefined
                ? //prettier-ignore
                  numberWithPeriod(
                    roundOff(
                      String(
                        (Number(props.state['power-consumption']['cooling-mode']) + Number(props.state['power-consumption']['heating-mode'])) / 1000
                      )
                    )
                  )
                : '--'}
            </Grid>
            <Grid
              item
              xs={3}
              className="subContents__content__body__data__powerGraphOpe__KWh">
              {trans('ViewQtonHyozan.body.unit')}
            </Grid>
            {/** グラフ */}
            <Grid
              item
              xs="auto"
              className="subContents__content__body__data__powerGraphOpe__graphOpeIcon--accordion">
              {/** 消費電力量グラフ */}
              <BarGraphButton
                isHover={isHover && hoveringIcon === 'bar'}
                setHoveringIcon={setHoveringIcon}
                isIconDisabled={props.state['error-code'] === ''} //エラーコードが空文字の場合対象のユニットは無課金GWのユニットなので無効化
                onClick={e => {
                  e.stopPropagation();
                  props.setGraphParam(
                    'chiller',
                    props.state['unit-id'],
                    'bar',
                    props.state['unit-name'],
                    'bar'
                  );
                  props.setSelectedUnit(props.state['unit-id']);
                  navigate(
                    `/pages/my-site/site-detail/${props.siteID}/state/chiller/${props.state['unit-id']}`
                  );
                }}
              />
              {/** 温度グラフ */}
              <LineGraphButton
                isHover={isHover && hoveringIcon === 'line'}
                setHoveringIcon={setHoveringIcon}
                isIconDisabled={props.state['error-code'] === ''} //エラーコードが空文字の場合対象のユニットは無課金GWのユニットなので無効化
                onClick={e => {
                  e.stopPropagation();
                  props.setGraphParam(
                    'chiller',
                    props.state['unit-id'],
                    'line',
                    props.state['unit-name'],
                    'line'
                  );
                  props.setSelectedUnit(props.state['unit-id']);
                  navigate(
                    `/pages/my-site/site-detail/${props.siteID}/state/chiller/${props.state['unit-id']}`
                  );
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </AccordionSummary>
      {/* アコーディオン開き状態 */}
      <AccordionDetails className="subContents__content__body__data__accordionDetails">
        {/* 全体 */}
        <Grid
          container
          className="subContents__content__body__data__accordionDetails"
          onClick={() => {
            props.setGraphParam('');
          }}>
          {/* 状態値 */}
          <Grid item xs={9.5} container alignItems="flex-end">
            {/** 出口水温 */}
            <Grid item xs={4}>
              <AccordionDetailsState
                title={trans('ViewQtonHyozan.body.parameter.title.outletTemp')}
                value={`${trans(
                  'ViewQtonHyozan.body.parameter.value.unit.temp',
                  {
                    value:
                      props.state['outlet-water-temperature'] !== undefined &&
                      props.state['outlet-water-temperature'] !== ''
                        ? invertComma(
                            roundOff(props.state['outlet-water-temperature'], 1)
                          )
                        : '--',
                  }
                )}`}
              />
            </Grid>
            {/** 設定温度 */}
            <Grid item xs={4}>
              <AccordionDetailsState
                title={trans('ViewQtonHyozan.body.parameter.title.setTemp')}
                value={`${trans(
                  'ViewQtonHyozan.body.parameter.value.unit.temp',
                  {
                    value:
                      props.state['preset-temperature'] !== undefined &&
                      props.state['preset-temperature'] !== ''
                        ? invertComma(
                            roundOff(props.state['preset-temperature'], 1)
                          )
                        : '--',
                  }
                )}`}
              />
            </Grid>
            {/** 運転モード */}
            <Grid item xs={4}>
              <AccordionDetailsState
                title={trans('ViewQtonHyozan.body.parameter.title.mode')}
                value={operationModeName(props.state['operation-mode'])}
              />
            </Grid>
          </Grid>
          {/** グラフ */}
          <Grid item xs={2.5}>
            {/** 消費電力量グラフ */}
            <BarGraphButton
              isHover={isHover && hoveringIcon === 'bar'}
              setHoveringIcon={setHoveringIcon}
              isIconDisabled={props.state['error-code'] === ''} //エラーコードが空文字の場合対象のユニットは無課金GWのユニットなので無効化
              onClick={e => {
                e.stopPropagation();
                props.setGraphParam(
                  'chiller',
                  props.state['unit-id'],
                  'bar',
                  props.state['unit-name'],
                  'bar'
                );
                props.setSelectedUnit(props.state['unit-id']);
                navigate(
                  `/pages/my-site/site-detail/${props.siteID}/state/chiller/${props.state['unit-id']}`
                );
              }}
            />
            {/** 温度グラフ */}
            <LineGraphButton
              isHover={isHover && hoveringIcon === 'line'}
              setHoveringIcon={setHoveringIcon}
              isIconDisabled={props.state['error-code'] === ''} //エラーコードが空文字の場合対象のユニットは無課金GWのユニットなので無効化
              onClick={e => {
                e.stopPropagation();
                props.setGraphParam(
                  'chiller',
                  props.state['unit-id'],
                  'line',
                  props.state['unit-name'],
                  'line'
                );
                props.setSelectedUnit(props.state['unit-id']);
                navigate(
                  `/pages/my-site/site-detail/${props.siteID}/state/chiller/${props.state['unit-id']}`
                );
              }}
            />
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
/**
 * Hyozan室外機
 * @param props props
 * - siteID サイトID
 * - state 状態値
 * - selectedUnit 選択状態の空調機
 * - setSelectedUnit 選択状態の空調機のset関数
 * - hoveringUnit ホバー中のユニット
 * - setHoveringUnit ホバー中ユニットのset関数
 * - pressureUnit 圧力単位
 * - iuIDList 室外機に登録されている室内機のID一覧
 * - setUnitTypeFilter フィルタのset関数
 * - setGraphParam グラフのパラメータのset関数
 * @returns JSX
 */
const HyozanOUView = (props: {
  siteID: string;
  state: apiIF.resParameters.rtOUStates;
  selectedUnit: string;
  setSelectedUnit: React.Dispatch<React.SetStateAction<string>>;
  hoveringUnit: string;
  setHoveringUnit: React.Dispatch<React.SetStateAction<string>>;
  pressureUnit: apiIF.res.getSettingUser['unit']['pressure'];
  iuIDList: string[];
  setUnitTypeFilter: React.Dispatch<
    React.SetStateAction<mysite.filterProps['unitType']>
  >;
  setGraphParam: (
    graphTarget: GraphTargetType,
    graphTargetID?: string,
    graphType?: GraphFormType,
    graphTitle?: string
  ) => void;
}) => {
  const trans = useTranslation().t; //翻訳関数
  const navigate = useNavigate();

  //prettier-ignore
  const [hoveringIcon, setHoveringIcon] = useState<'' | 'bar' | 'line'>(''); //ホバー状態のアイコン

  /** 選択されているかどうか */
  const isSelected = props.state['unit-id'] === props.selectedUnit;
  /** ホバーされているかどうか */
  const isHover = props.state['unit-id'] === props.hoveringUnit;
  /** エラーユニットフラグ エラー有:true エラー無:false */
  const errUnitFlg =
    props.state['error-code'] !== undefined &&
    props.state['error-code'] !== '' && //無課金状態
    props.state['error-code'] !== '00';

  /** 運転/停止アイコン */
  const runStopIconSrc = (
    runStopState: apiIF.resParameters.rtOUStates['run-stop']
  ) => {
    switch (runStopState) {
      case 'run':
        return iconWhite.FREEZER_ON.src;
      case 'stop':
        return iconBlack.FREEZER_OFF.src;
      default:
        return iconBlack.MISSING.src;
    }
  };
  /** 運転/停止アイコン用クラス名 */
  const runStopIconClassName = (
    runStopState: apiIF.resParameters.rtOUStates['run-stop']
  ) => {
    switch (runStopState) {
      case 'run':
        return 'subContents__content__body__data__status__runStopIcon--run';
      case 'stop':
        return 'subContents__content__body__data__status__runStopIcon--stop';
      default:
        return 'subContents__content__body__data__status__runStopIcon--stop';
    }
  };

  /**
   * 空調機が選択状態か判定
   * @param unitID グループID
   */
  const selectHandle = (unitID: string) => {
    if (unitID === props.selectedUnit) {
      props.setSelectedUnit('');
    } else {
      props.setSelectedUnit(unitID);
    }
  };

  return (
    <Grid
      container
      alignItems="center"
      // ユニット選択処理
      onClick={() => {
        selectHandle(props.state['unit-id']);
        //未選択から選択状態になった時のみ実施
        if (props.state['unit-id'] !== props.selectedUnit) {
          props.setGraphParam(
            'hyozan',
            props.state['unit-id'],
            'bar',
            props.state['unit-name']
          );
          navigate(
            `/pages/my-site/site-detail/${props.siteID}/state/hyozan/${props.state['unit-id']}`
          );
        }
      }}
      onMouseEnter={() => {
        props.setHoveringUnit(props.state['unit-id']);
      }}
      onMouseLeave={() => {
        props.setHoveringUnit('');
      }}
      className={(() => {
        // リンク表示
        if (
          // コンデンシングユニット選択
          isSelected ||
          // コンデンシングユニットをホバー
          isHover ||
          //接続ユニットクーラ選択
          props.iuIDList.includes(props.selectedUnit) ||
          // 接続ユニットクーラをホバー
          props.iuIDList.includes(props.hoveringUnit)
        ) {
          return 'subContents__content__body__data subContents__content__body__data--linked';
        }
        return 'subContents__content__body__data';
      })()}>
      {/* リンクアイコン */}
      <Box className="subContents__content__body__data__linkedIconBorder">
        <img
          src={iconBlack.LINKED.src}
          alt={`${trans(
            `ViewQtonHyozan.body.image.alternativeText.${iconBlack.LINKED.alt}`
          )}`}
          className="subContents__content__body__data__linkedIconBorder__image"
        />
      </Box>
      {/** 空調機名 */}
      <Grid
        item
        xs={gridXsAirUnit}
        title={props.state['unit-name']}
        onClick={() => props.setUnitTypeFilter('hccvq')}
        className="subContents__content__body__data__category">
        {props.state['unit-name']}
      </Grid>
      {/** 運転/停止、異常表示、高圧低圧 */}
      <Grid
        item
        xs={gridXsAirState}
        container
        columnSpacing={1.5}
        alignItems="center">
        {/** 運転/停止 */}
        <Grid item xs="auto">
          <Box className={runStopIconClassName(props.state['run-stop'])}>
            <img
              src={runStopIconSrc(props.state['run-stop'])}
              alt={`${trans(
                'ViewQtonHyozan.body.image.alternativeText.runStopIcon'
              )}`}
              className="subContents__content__body__data__status__runStopIcon__image"
            />
          </Box>
        </Grid>
        {/** 異常表示 */}
        {/** 異常時のみ表示する */}
        {errUnitFlg && (
          <Grid item xs="auto">
            <img
              src={
                isHover && hoveringIcon === ''
                  ? iconWhite.ERROR.src //空調機をホバー かつ グラフアイコンをホバー
                  : iconYellow.ERROR.src
              }
              alt={`${trans(
                'ViewQtonHyozan.body.image.alternativeText.errIcon'
              )}`}
              className="subContents__content__body__data__status__errorIconForUnit"
            />
          </Grid>
        )}
        {/** 高圧低圧 */}
        <Grid item xs="auto">
          <Stack spacing={0.5}>
            <Box className="subContents__content__body__data__status__pressure">
              {`${trans(
                'ViewQtonHyozan.body.parameter.title.compressorHighPressure'
              )}: ${trans(
                props.pressureUnit === 'MPa'
                  ? 'ViewQtonHyozan.body.parameter.value.unit.pressure.mpa'
                  : 'ViewQtonHyozan.body.parameter.value.unit.pressure.bar',
                {
                  value:
                    props.state['high-pressure'] !== undefined &&
                    props.state['high-pressure'] !== ''
                      ? invertComma(roundOff(props.state['high-pressure'], 1))
                      : '--',
                }
              )}`}
            </Box>
            <Box className="subContents__content__body__data__status__pressure">
              {`${trans(
                'ViewQtonHyozan.body.parameter.title.compressorLowPressure'
              )}: ${trans(
                props.pressureUnit === 'MPa'
                  ? 'ViewQtonHyozan.body.parameter.value.unit.pressure.mpa'
                  : 'ViewQtonHyozan.body.parameter.value.unit.pressure.bar',
                {
                  value:
                    props.state['low-pressure'] !== undefined &&
                    props.state['low-pressure'] !== ''
                      ? invertComma(roundOff(props.state['low-pressure'], 1))
                      : '--',
                }
              )}`}
            </Box>
          </Stack>
        </Grid>
      </Grid>
      {/** 消費電力量、グラフ */}
      <Grid
        item
        xs={gridXsAirKwh}
        container
        justifyContent="flex-end"
        className="subContents__content__body__data__powerGraphOpe">
        {/** 消費電力量 */}
        <Grid
          item
          xs="auto"
          className="subContents__content__body__data__powerGraphOpe__power">
          {props.state['power-consumption'] !== undefined
            ? //prettier-ignore
              numberWithPeriod(
                roundOff(
                  String(Number(props.state['power-consumption']['cooling-mode']) / 1000)
                )
              )
            : '--'}
        </Grid>
        <Grid
          item
          xs={3}
          className="subContents__content__body__data__powerGraphOpe__KWh">
          {trans('ViewQtonHyozan.body.unit')}
        </Grid>
        {/** グラフ */}
        <Grid
          item
          xs="auto"
          className="subContents__content__body__data__powerGraphOpe__graphOpeIcon">
          {/** 消費電力量グラフ */}
          <BarGraphButton
            isHover={isHover && hoveringIcon === 'bar'}
            setHoveringIcon={setHoveringIcon}
            isIconDisabled={props.state['error-code'] === ''} //エラーコードが空文字の場合対象のユニットは無課金GWのユニットなので無効化
            onClick={e => {
              e.stopPropagation();
              props.setGraphParam(
                'hyozan',
                props.state['unit-id'],
                'bar',
                props.state['unit-name']
              );
              props.setSelectedUnit(props.state['unit-id']);
              navigate(
                `/pages/my-site/site-detail/${props.siteID}/state/hyozan/${props.state['unit-id']}`
              );
            }}
          />
          {/** 圧力グラフ */}
          <LineGraphButton
            isHover={isHover && hoveringIcon === 'line'}
            setHoveringIcon={setHoveringIcon}
            isIconDisabled={props.state['error-code'] === ''} //エラーコードが空文字の場合対象のユニットは無課金GWのユニットなので無効化
            onClick={e => {
              e.stopPropagation();
              props.setGraphParam(
                'hyozan',
                props.state['unit-id'],
                'line',
                props.state['unit-name']
              );
              props.setSelectedUnit(props.state['unit-id']);
              navigate(
                `/pages/my-site/site-detail/${props.siteID}/state/hyozan/${props.state['unit-id']}`
              );
            }}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
/**
 * Modbus室外機
 * @param props props
 * - siteID サイトID
 * - state 状態値
 * - selectedUnit 選択状態の空調機
 * - setSelectedUnit 選択状態の空調機のset関数
 * - hoveringUnit ホバー中のユニット
 * - setHoveringUnit ホバー中ユニットのset関数
 * - pressureUnit 圧力単位
 * - iuIDList 室外機に登録されている室内機のID一覧
 * - setUnitTypeFilter フィルタのset関数
 * - setGraphParam グラフのパラメータのset関数
 * @returns JSX
 */
const ModbusOUView = (props: {
  siteID: string;
  state: apiIF.resParameters.modbusOUStates;
  selectedUnit: string;
  setSelectedUnit: React.Dispatch<React.SetStateAction<string>>;
  hoveringUnit: string;
  setHoveringUnit: React.Dispatch<React.SetStateAction<string>>;
  pressureUnit: apiIF.res.getSettingUser['unit']['pressure'];
  iuIDList: string[];
  setUnitTypeFilter: React.Dispatch<
    React.SetStateAction<mysite.filterProps['unitType']>
  >;
  setGraphParam: (
    graphTarget: GraphTargetType,
    graphTargetID?: string,
    graphType?: GraphFormType,
    graphTitle?: string
  ) => void;
}) => {
  const trans = useTranslation().t; //翻訳関数
  const navigate = useNavigate();

  //prettier-ignore
  const [hoveringIcon, setHoveringIcon] = useState<'' | 'bar' | 'line'>(''); //ホバー状態のアイコン

  /** 選択されているかどうか */
  const isSelected = props.state['outdoor-id'] === props.selectedUnit;
  /** ホバーされているかどうか */
  const isHover = props.state['outdoor-id'] === props.hoveringUnit;
  /** エラーユニットフラグ エラー有:true エラー無:false */
  const errUnitFlg =
    props.state['error-code'] !== undefined &&
    props.state['error-code'] !== '' && //無課金状態
    props.state['error-code'] !== '00';

  /** 運転/停止アイコン */
  const runStopIconSrc = (
    runStopState: apiIF.resParameters.modbusOUStates['run-stop']
  ) => {
    switch (runStopState) {
      case 'run':
        return iconWhite.FREEZER_ON.src;
      case 'stop':
        return iconBlack.FREEZER_OFF.src;
      default:
        return iconBlack.MISSING.src;
    }
  };
  /** 運転/停止アイコン用クラス名 */
  const runStopIconClassName = (
    runStopState: apiIF.resParameters.rtOUStates['run-stop']
  ) => {
    switch (runStopState) {
      case 'run':
        return 'subContents__content__body__data__status__runStopIcon--run';
      case 'stop':
        return 'subContents__content__body__data__status__runStopIcon--stop';
      default:
        return 'subContents__content__body__data__status__runStopIcon--stop';
    }
  };

  /**
   * 空調機が選択状態か判定
   * @param unitID グループID
   */
  const selectHandle = (unitID: string) => {
    if (unitID === props.selectedUnit) {
      props.setSelectedUnit('');
    } else {
      props.setSelectedUnit(unitID);
    }
  };

  return (
    <Grid
      container
      alignItems="center"
      // ユニット選択処理
      onClick={() => {
        selectHandle(props.state['outdoor-id']);
        //未選択から選択状態になった時のみ実施
        if (props.state['outdoor-id'] !== props.selectedUnit) {
          props.setGraphParam(
            'modbus',
            props.state['outdoor-id'],
            'bar',
            props.state['outdoor-unit-name']
          );
          navigate(
            `/pages/my-site/site-detail/${props.siteID}/state/modbus/${props.state['outdoor-id']}`
          );
        }
      }}
      onMouseEnter={() => {
        props.setHoveringUnit(props.state['outdoor-id']);
      }}
      onMouseLeave={() => {
        props.setHoveringUnit('');
      }}
      className={(() => {
        // リンク表示
        if (
          // コンデンシングユニット選択
          isSelected ||
          // コンデンシングユニットをホバー
          isHover ||
          //接続ユニットクーラ選択
          props.iuIDList.includes(props.selectedUnit) ||
          // 接続ユニットクーラをホバー
          props.iuIDList.includes(props.hoveringUnit)
        ) {
          return 'subContents__content__body__data subContents__content__body__data--linked';
        }
        return 'subContents__content__body__data';
      })()}>
      {/* リンクアイコン */}
      <Box className="subContents__content__body__data__linkedIconBorder">
        <img
          src={iconBlack.LINKED.src}
          alt={`${trans(
            `ViewQtonHyozan.body.image.alternativeText.${iconBlack.LINKED.alt}`
          )}`}
          className="subContents__content__body__data__linkedIconBorder__image"
        />
      </Box>
      {/** 空調機名 */}
      <Grid
        item
        xs={gridXsAirUnit}
        title={props.state['outdoor-unit-name']}
        onClick={() => props.setUnitTypeFilter('hccvq')}
        className="subContents__content__body__data__category">
        {props.state['outdoor-unit-name']}
      </Grid>
      {/** 運転/停止、異常表示、高圧低圧 */}
      <Grid
        item
        xs={gridXsAirState}
        container
        columnSpacing={1}
        alignItems="center">
        {/** 運転/停止 */}
        <Grid item xs="auto">
          <Box className={runStopIconClassName(props.state['run-stop'])}>
            <img
              src={runStopIconSrc(props.state['run-stop'])}
              alt={`${trans(
                'ViewQtonHyozan.body.image.alternativeText.runStopIcon'
              )}`}
              className="subContents__content__body__data__status__runStopIcon__image"
            />
          </Box>
        </Grid>
        {/** 異常表示 */}
        {/** 異常時のみ表示する */}
        {errUnitFlg && (
          <Grid item xs="auto">
            <img
              src={
                isHover && hoveringIcon === ''
                  ? iconWhite.ERROR.src //空調機をホバー かつ グラフアイコンをホバー
                  : iconYellow.ERROR.src
              }
              alt={`${trans(
                'ViewQtonHyozan.body.image.alternativeText.errIcon'
              )}`}
              className="subContents__content__body__data__status__errorIconForUnit"
            />
          </Grid>
        )}
        {/** 高圧低圧 */}
        <Grid item xs="auto">
          <Stack spacing={0.5}>
            <Box className="subContents__content__body__data__status__pressure">
              {`${trans(
                'ViewQtonHyozan.body.parameter.title.compressorHighPressure'
              )}: ${trans(
                props.pressureUnit === 'MPa'
                  ? 'ViewQtonHyozan.body.parameter.value.unit.pressure.mpa'
                  : 'ViewQtonHyozan.body.parameter.value.unit.pressure.bar',
                {
                  value:
                    props.state['high-pressure'] !== undefined &&
                    props.state['high-pressure'] !== ''
                      ? invertComma(roundOff(props.state['high-pressure'], 1))
                      : '--',
                }
              )}`}
            </Box>
            <Box className="subContents__content__body__data__status__pressure">
              {`${trans(
                'ViewQtonHyozan.body.parameter.title.compressorLowPressure'
              )}: ${trans(
                props.pressureUnit === 'MPa'
                  ? 'ViewQtonHyozan.body.parameter.value.unit.pressure.mpa'
                  : 'ViewQtonHyozan.body.parameter.value.unit.pressure.bar',
                {
                  value:
                    props.state['low-pressure'] !== undefined &&
                    props.state['low-pressure'] !== ''
                      ? invertComma(roundOff(props.state['low-pressure'], 1))
                      : '--',
                }
              )}`}
            </Box>
          </Stack>
        </Grid>
      </Grid>
      {/** 消費電力量、グラフ */}
      <Grid
        item
        xs={gridXsAirKwh}
        container
        justifyContent="flex-end"
        className="subContents__content__body__data__powerGraphOpe">
        {/** 消費電力量 */}
        <Grid
          item
          xs="auto"
          className="subContents__content__body__data__powerGraphOpe__power">
          {numberWithPeriod(
            roundOff(
              String(
                Number(props.state['power-consumption']['cooling-mode']) / 1000
              )
            )
          )}
        </Grid>
        <Grid
          item
          xs={3}
          className="subContents__content__body__data__powerGraphOpe__KWh">
          {trans('ViewQtonHyozan.body.unit')}
        </Grid>
        {/** グラフ */}
        <Grid
          item
          xs="auto"
          className="subContents__content__body__data__powerGraphOpe__graphOpeIcon">
          {/** 消費電力量グラフ */}
          <BarGraphButton
            isHover={isHover && hoveringIcon === 'bar'}
            setHoveringIcon={setHoveringIcon}
            isIconDisabled={props.state['error-code'] === ''} //エラーコードが空文字の場合対象のユニットは無課金GWのユニットなので無効化
            onClick={e => {
              e.stopPropagation();
              props.setGraphParam(
                'modbus',
                props.state['outdoor-id'],
                'bar',
                props.state['outdoor-unit-name']
              );
              props.setSelectedUnit(props.state['outdoor-id']);
              navigate(
                `/pages/my-site/site-detail/${props.siteID}/state/modbus/${props.state['outdoor-id']}`
              );
            }}
          />
          {/** 圧力グラフ */}
          <LineGraphButton
            isHover={isHover && hoveringIcon === 'line'}
            setHoveringIcon={setHoveringIcon}
            isIconDisabled={props.state['error-code'] === ''} //エラーコードが空文字の場合対象のユニットは無課金GWのユニットなので無効化
            onClick={e => {
              e.stopPropagation();
              props.setGraphParam(
                'modbus',
                props.state['outdoor-id'],
                'line',
                props.state['outdoor-unit-name']
              );
              props.setSelectedUnit(props.state['outdoor-id']);
              navigate(
                `/pages/my-site/site-detail/${props.siteID}/state/modbus/${props.state['outdoor-id']}`
              );
            }}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
/**
 * HCCVQ室内機
 * @param props props
 * - state 状態値
 * - selectedUnit 選択状態の空調機
 * - setSelectedUnit 選択状態の空調機のset関数
 * - hoveringUnit ホバー中のユニット
 * - setHoveringUnit ホバー中ユニットのset関数
 * - ouID 室内機が登録されいる室外機のID
 * - destination アコーディオンを開いた時の遷移先
 * - setUnitTypeFilter フィルタのset関数
 * - graphTarget グラフの対象機種
 * - setGraphParam グラフのパラメータのset関数
 * @returns JSX
 */
const HCCVQIUView = (props: {
  state: apiIF.resParameters.modbusIUStates | apiIF.resParameters.rtIUStates;
  selectedUnit: string;
  setSelectedUnit: React.Dispatch<React.SetStateAction<string>>;
  hoveringUnit: string;
  setHoveringUnit: React.Dispatch<React.SetStateAction<string>>;
  ouID: string;
  destination: string;
  setUnitTypeFilter: React.Dispatch<
    React.SetStateAction<mysite.filterProps['unitType']>
  >;
  graphTarget: 'hyozanIndoor' | 'modbusIndoor';
  setGraphParam: (
    graphTarget: GraphTargetType,
    graphTargetID?: string,
    graphType?: GraphFormType,
    graphTitle?: string
  ) => void;
}) => {
  const trans = useTranslation().t; //翻訳関数
  const navigate = useNavigate();

  //prettier-ignore
  const [hoveringIcon, setHoveringIcon] = useState<'' | 'bar' | 'line'>(''); //ホバー状態のアイコン

  /** 選択されているかどうか */
  const isSelected = props.state['unit-id'] === props.selectedUnit;
  /** ホバーされているかどうか */
  const isHover = props.state['unit-id'] === props.hoveringUnit;

  /** Modbus ユニットのエラーが発生しているかどうか　*/
  const hasModbusError =
    props.state &&
    'error-code' in props.state &&
    props.state['error-code'] !== '' && //無課金
    props.state['error-code'] !== '00';

  /**　Hyozan 室内機ユニットが未接続かどうか */
  const hasHyozanConnectionState =
    props.state &&
    'connection-status' in props.state &&
    props.state['connection-status'] === 'notConnected';

  /**　温度が表示できるかどうか　*/
  const showHyozanUnitStates =
    (props.state &&
      'error-code' in props.state && //MODBUSユニットが正常状態の場合
      props.state['error-code'] === '00') ||
    (props.state &&
      'connection-status' in props.state && //HYOZAN室内機ユニットが正常状態の場合
      props.state['connection-status'] === 'connected');

  /** Modbus ユニットとHyozan 室内機ユニットでエラーが発生しているかどうか */
  const errUnitFlg = hasModbusError || hasHyozanConnectionState;

  /** グラフアイコン無効かどうか */
  const notShowGraphFlg =
    (props.state &&
      'error-code' in props.state && //MODBUSユニットが正常状態の場合
      props.state['error-code'] === '') ||
    (props.state &&
      'connection-status' in props.state && //HYOZAN室内機ユニットが正常状態の場合
      props.state['connection-status'] === '');

  /** 運転/停止アイコン */
  const runStopIconSrc = (
    runStopState: apiIF.resParameters.modbusOUStates['run-stop']
  ) => {
    switch (runStopState) {
      case 'run':
        return iconWhite.FREEZER_ON.src;
      case 'stop':
        return iconBlack.FREEZER_OFF.src;
      default:
        return iconBlack.MISSING.src;
    }
  };
  /** 運転/停止アイコン用クラス名 */
  const runStopIconClassName = (
    runStopState: apiIF.resParameters.rtOUStates['run-stop']
  ) => {
    switch (runStopState) {
      case 'run':
        return 'subContents__content__body__data__status__runStopIcon--run';
      case 'stop':
        return 'subContents__content__body__data__status__runStopIcon--stop';
      default:
        return 'subContents__content__body__data__status__runStopIcon--stop';
    }
  };
  /** 温度差用クラス名 */
  const differenceTempClassName = (
    level: apiIF.resParameters.modbusIUStates['difference-level']
  ) => {
    switch (level) {
      case 'safety':
        return 'subContents__content__body__data__status__differenceTemp--safety';
      case 'carefully(low)':
        return 'subContents__content__body__data__status__differenceTemp--carefullyLow';
      case 'carefully(high)':
        return 'subContents__content__body__data__status__differenceTemp--carefullyHigh';
      case 'warning':
        return 'subContents__content__body__data__status__differenceTemp--warning';
      default:
        return '';
    }
  };

  /**
   * アコーディオンの状態変更時
   * @param event
   * @param isExpanded
   */
  const handleChange = (event: React.SyntheticEvent, isExpanded: boolean) => {
    if (isExpanded) {
      props.setSelectedUnit(props.state['unit-id']);
      navigate(props.destination);
    } else {
      props.setSelectedUnit('');
    }
  };

  /**
   * 温度差を算出
   * @param insideTemp 庫内温度
   * @param presetTemp 設定温度
   * @returns 温度差
   */
  const differenceTemp = (insideTemp: string, presetTemp: string) => {
    if (insideTemp === '' || presetTemp === '') return '--';

    const difference = Number(insideTemp) - Number(presetTemp);
    const convertDifference = invertComma(roundOff(String(difference), 1));

    if (difference >= 0) {
      return `+${convertDifference}`;
    } else {
      return convertDifference;
    }
  };

  return (
    <Accordion
      disableGutters={true}
      expanded={isSelected}
      onChange={handleChange}
      onMouseEnter={() => props.setHoveringUnit(props.state['unit-id'])}
      onMouseLeave={() => props.setHoveringUnit('')}
      className={`${(() => {
        if (isSelected) {
          return (
            'subContents__content__body__data--selected ' +
            'subContents__content__body__data--linked ' +
            'subContents__content__body__data--linked--accordion'
          );
        }
        if (
          props.selectedUnit.includes(props.ouID) ||
          props.ouID === props.selectedUnit ||
          props.ouID === props.hoveringUnit ||
          props.hoveringUnit.includes(props.ouID)
        ) {
          return (
            'subContents__content__body__data ' +
            'subContents__content__body__data--accordion ' +
            'subContents__content__body__data--linked ' +
            'subContents__content__body__data--linked--accordion'
          );
        }
        return (
          'subContents__content__body__data ' +
          'subContents__content__body__data--accordion'
        );
      })()}`}>
      {/* アコーディオン閉じ状態 */}
      <AccordionSummary
        aria-controls={`${props.state['indoor-unit-name']}-content`}
        className="subContents__content__body__data__accordionSummary">
        {/* リンクアイコン */}
        <Box className="subContents__content__body__data__linkedIconBorder">
          <img
            src={iconBlack.LINKED.src}
            alt={`${trans(
              `ViewQtonHyozan.body.image.alternativeText.${iconBlack.LINKED.alt}`
            )}`}
            className="subContents__content__body__data__linkedIconBorder__image"
          />
        </Box>
        <Grid
          container
          alignItems="center"
          className="subContents__content__body__data__accordionSummary__accordionData">
          {/** 空調機名 */}
          <Grid
            item
            xs={gridXsAirUnit}
            title={props.state['indoor-unit-name']}
            onClick={() => props.setUnitTypeFilter('hccvq')}
            className="subContents__content__body__data__category">
            {props.state['indoor-unit-name']}
          </Grid>
          {/** 運転/停止、温度、温度差 */}
          <Grid
            item
            xs={gridXsAirState}
            container
            columnSpacing={1.5}
            alignItems="center">
            {/** 運転/停止 */}
            <Grid item xs="auto">
              <Box className={runStopIconClassName(props.state['run-stop'])}>
                {props.state['inside-temperature'] !== undefined &&
                props.state['preset-temperature'] !== undefined &&
                showHyozanUnitStates === true ? ( //無課金orエラー発生時は表示しない
                  <img
                    src={runStopIconSrc(props.state['run-stop'])}
                    alt={`${trans(
                      'ViewQtonHyozan.body.image.alternativeText.runStopIcon'
                    )}`}
                    className="subContents__content__body__data__status__runStopIcon__image"
                  />
                ) : (
                  <img
                    src={iconBlack.MISSING.src}
                    alt={`${trans(
                      'ViewQtonHyozan.body.image.alternativeText.runStopIcon'
                    )}`}
                    className="subContents__content__body__data__status__runStopIcon__image"
                  />
                )}
              </Box>
            </Grid>
            {/** 異常表示 */}
            {/** 異常時のみ表示する */}
            {errUnitFlg && (
              <Grid item xs="auto">
                <img
                  src={
                    isHover && hoveringIcon === ''
                      ? iconWhite.ERROR.src //空調機をホバー かつ グラフアイコンをホバー
                      : iconYellow.ERROR.src
                  }
                  alt={`${trans(
                    'ViewQtonHyozan.body.image.alternativeText.errIcon'
                  )}`}
                  className="subContents__content__body__data__status__errorIconForUnit"
                />
              </Grid>
            )}
            {/** 温度 */}
            <Grid item xs="auto">
              <Stack spacing={0.5}>
                <Box className="subContents__content__body__data__status__temperature">
                  {trans('ViewQtonHyozan.body.parameter.value.unit.temp', {
                    value:
                      props.state['inside-temperature'] !== undefined &&
                      showHyozanUnitStates === true //無課金orエラー発生時は表示しない
                        ? invertComma(
                            roundOff(props.state['inside-temperature'], 1)
                          )
                        : '--',
                  })}
                </Box>
                <Box className="subContents__content__body__data__status__temperature">
                  {trans('ViewQtonHyozan.body.parameter.value.unit.temp', {
                    value:
                      props.state['preset-temperature'] !== undefined &&
                      showHyozanUnitStates === true //無課金orエラー発生時は表示しない
                        ? invertComma(
                            roundOff(props.state['preset-temperature'], 1)
                          )
                        : '--',
                  })}
                </Box>
              </Stack>
            </Grid>
            {/** 温度差 */}
            {
              <Grid item xs="auto">
                {props.state['inside-temperature'] !== undefined &&
                props.state['preset-temperature'] !== undefined &&
                showHyozanUnitStates === true ? ( //無課金orエラー発生時は表示しない
                  <Box
                    className={`subContents__content__body__data__status__differenceTemp ${differenceTempClassName(
                      props.state['difference-level']
                    )}`}>
                    {trans('ViewQtonHyozan.body.parameter.value.unit.temp', {
                      value: differenceTemp(
                        props.state['inside-temperature'],
                        props.state['preset-temperature']
                      ),
                    })}
                  </Box>
                ) : (
                  <Box
                    className={`subContents__content__body__data__status__differenceTemp__disconnect`}>
                    {trans('ViewQtonHyozan.body.parameter.value.unit.temp', {
                      value: '--',
                    })}
                  </Box>
                )}
              </Grid>
            }
          </Grid>
          {/** グラフ */}
          <Grid
            item
            xs={gridXsAirKwh}
            container
            justifyContent="flex-end"
            className="subContents__content__body__data__powerGraphOpe">
            {/** 温度グラフ */}
            <Grid
              item
              xs="auto"
              className="subContents__content__body__data__powerGraphOpe__graphOpeIcon--accordion">
              <LineGraphButton
                isHover={isHover && hoveringIcon === 'line'}
                setHoveringIcon={setHoveringIcon}
                isIconDisabled={notShowGraphFlg}
                onClick={() => {
                  props.setGraphParam(
                    props.graphTarget,
                    props.state['unit-id'],
                    'line',
                    props.state['indoor-unit-name']
                  );
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails className="subContents__content__body__data__accordionDetails">
        <Grid
          container
          className="subContents__content__body__data__accordionDetails"
          justifyContent="flex-end">
          {/** グラフ*/}
          <Grid item xs={2}>
            <LineGraphButton
              isHover={isHover && hoveringIcon === 'line'}
              setHoveringIcon={setHoveringIcon}
              isIconDisabled={notShowGraphFlg}
              onClick={() => {
                props.setGraphParam(
                  props.graphTarget,
                  props.state['unit-id'],
                  'line',
                  props.state['indoor-unit-name']
                );
              }}
            />
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
/**
 * Q-ton
 * @param props props
 * - siteID サイトID
 * - state 状態値
 * - selectedUnit 選択状態の空調機
 * - setSelectedUnit 選択状態の空調機のset関数
 * - hoveringUnit ホバー中のユニット
 * - setHoveringUnit ホバー中ユニットのset関数
 * - childUnitInfo 親機登録がされている機器のユニットIDと親機のID情報
 * - setUnitTypeFilter フィルタのset関数
 * - setGraphParam グラフのパラメータのset関数
 * @returns JSX
 */
const QtonView = (props: {
  siteID: string;
  state: apiIF.resParameters.rtOUStates;
  selectedUnit: string;
  setSelectedUnit: React.Dispatch<React.SetStateAction<string>>;
  hoveringUnit: string;
  setHoveringUnit: React.Dispatch<React.SetStateAction<string>>;
  childUnitInfo: apiIF.resParameters.rtUnitInformations[];
  setUnitTypeFilter: React.Dispatch<
    React.SetStateAction<mysite.filterProps['unitType']>
  >;
  setGraphParam: (
    graphTarget: GraphTargetType,
    graphTargetID?: string,
    graphType?: GraphFormType,
    graphTitle?: string,
    tabType?: 'bar' | 'line' | 'diagram',
    childUnitInfo?: apiIF.resParameters.rtUnitInformations[] //温度グラフの場合のみ
  ) => void;
}) => {
  const trans = useTranslation().t; //翻訳関数
  const navigate = useNavigate();

  //prettier-ignore
  const [hoveringIcon, setHoveringIcon] = useState<'' | 'bar' | 'line'>(''); //ホバー状態のアイコン

  /** 選択されているかどうか */
  const isSelected = props.state['unit-id'] === props.selectedUnit;
  /** ホバーされているかどうか */
  const isHover = props.state['unit-id'] === props.hoveringUnit;
  /** エラーユニットフラグ エラー有:true エラー無:false */
  const errUnitFlg =
    props.state['error-code'] !== undefined &&
    props.state['error-code'] !== '' && //無課金状態
    props.state['error-code'] !== '00';
  /** 対象の親機に登録されている子機の情報 */
  const childInfo = props.childUnitInfo.filter(item => {
    return item['parent-unit'] === props.state['unit-id'];
  });
  /** 対象の親機に接続されている子機のID一覧 */
  const childIDs = childInfo.map(value => value['unit-id']);

  /** 運転/停止アイコン */
  const runStopIconSrc = (
    runStopState: apiIF.resParameters.rtOUStates['run-stop']
  ) => {
    switch (runStopState) {
      case 'run':
        return iconWhite.HOT_WATER_ON.src;
      case 'stop':
        return iconBlack.HOT_WATER_OFF.src;
      case 'pause':
        return iconBlack.HOT_WATER_OFF.src;
      default:
        return iconBlack.MISSING.src;
    }
  };
  /** 運転/停止アイコン用クラス名 */
  const runStopIconClassName = (
    runStopState: apiIF.resParameters.rtOUStates['run-stop']
  ) => {
    switch (runStopState) {
      case 'run':
        return 'subContents__content__body__data__status__runStopIcon--run';
      case 'stop':
        return 'subContents__content__body__data__status__runStopIcon--stop';
      case 'pause':
        return 'subContents__content__body__data__status__runStopIcon--stop';
      default:
        return 'subContents__content__body__data__status__runStopIcon--stop';
    }
  };
  /**
   * アコーディオンのクラス名を決定する関数
   * @param unitID 対象ユニットのID
   * @return クラス名
   */
  const accordionClassName = (unitID: string) => {
    if (unitID === props.selectedUnit) {
      //子機が未登録の場合は実線、登録されている場合は点線の枠にする
      if (childIDs.length === 0) {
        return 'subContents__content__body__data--selected';
      }
      return 'subContents__content__body__data--selected subContents__content__body__data--linked subContents__content__body__data--linked--accordion';
    }
    if (
      unitID === props.hoveringUnit ||
      childIDs.includes(props.selectedUnit) ||
      childIDs.includes(props.hoveringUnit)
    ) {
      return 'subContents__content__body__data subContents__content__body__data--accordion subContents__content__body__data--linked subContents__content__body__data--linked--accordion';
    }
    return 'subContents__content__body__data subContents__content__body__data--accordion';
  };

  /**
   * アコーディオンの状態変更時
   * @param event
   * @param isExpanded
   */
  const handleChange = (event: React.SyntheticEvent, isExpanded: boolean) => {
    if (isExpanded) {
      props.setSelectedUnit(props.state['unit-id']);
      navigate(
        `/pages/my-site/site-detail/${props.siteID}/state/qton/${props.state['unit-id']}`
      );
    } else {
      props.setSelectedUnit('');
    }
  };

  return (
    <Accordion
      disableGutters={true}
      expanded={isSelected}
      onChange={handleChange}
      onMouseEnter={() => props.setHoveringUnit(props.state['unit-id'])}
      onMouseLeave={() => props.setHoveringUnit('')}
      className={accordionClassName(props.state['unit-id'])}>
      {/* アコーディオン閉じ状態 */}
      <AccordionSummary
        aria-controls={`${props.state['unit-name']}-content`}
        className="subContents__content__body__data__accordionSummary">
        {/* 子機が登録されている場合、リンク表示 */}
        {childIDs.length > 0 && (
          <Box className="subContents__content__body__data__linkedIconBorder">
            <img
              src={iconBlack.LINKED.src}
              alt={`${trans(
                `ViewQtonHyozan.body.image.alternativeText.${iconBlack.LINKED.alt}`
              )}`}
              className="subContents__content__body__data__linkedIconBorder__image"
            />
          </Box>
        )}
        <Grid
          container
          alignItems="center"
          onClick={() => {
            props.setGraphParam('');
          }}
          className="subContents__content__body__data__accordionSummary__accordionData">
          {/** 空調機名 */}
          <Grid
            item
            xs={gridXsAirUnit}
            title={props.state['unit-name']}
            onClick={() => {
              props.setUnitTypeFilter('qton');
            }}
            className="subContents__content__body__data__category">
            {props.state['unit-name']}
          </Grid>
          {/** 運転/停止、異常表示 */}
          <Grid
            item
            xs={gridXsAirState}
            container
            columnSpacing={2}
            alignItems="center">
            {/** 運転/停止 */}
            <Grid item xs="auto">
              <Box className={runStopIconClassName(props.state['run-stop'])}>
                <img
                  src={runStopIconSrc(props.state['run-stop'])}
                  alt={`${trans(
                    'ViewQtonHyozan.body.image.alternativeText.runStopIcon'
                  )}`}
                  className="subContents__content__body__data__status__runStopIcon__image"
                />
              </Box>
            </Grid>
            {/** 異常表示 */}
            {/** 異常時のみ表示する */}
            {errUnitFlg && (
              <Grid item xs="auto">
                <img
                  src={
                    isHover && hoveringIcon === ''
                      ? iconWhite.ERROR.src
                      : iconYellow.ERROR.src
                  }
                  alt={`${trans(
                    'ViewQtonHyozan.body.image.alternativeText.errIcon'
                  )}`}
                  className="subContents__content__body__data__status__errorIconForUnit"
                />
              </Grid>
            )}
          </Grid>
          {/** 消費電力量、グラフ */}
          <Grid
            item
            xs={gridXsAirKwh}
            container
            justifyContent="flex-end"
            className="subContents__content__body__data__powerGraphOpe">
            {/** 消費電力量 */}
            <Grid
              item
              xs="auto"
              className="subContents__content__body__data__powerGraphOpe__power">
              {props.state['power-consumption'] !== undefined
                ? //prettier-ignore
                  numberWithPeriod(
                    roundOff(
                      String(
                        (Number(props.state['power-consumption']['cooling-mode']) + Number(props.state['power-consumption']['heating-mode'])) / 1000
                      )
                    )
                  )
                : '--'}
            </Grid>
            <Grid
              item
              xs={3}
              className="subContents__content__body__data__powerGraphOpe__KWh">
              {trans('ViewQtonHyozan.body.unit')}
            </Grid>
            {/** グラフ */}
            <Grid
              item
              xs="auto"
              className="subContents__content__body__data__powerGraphOpe__graphOpeIcon--accordion">
              {/** 消費電力量グラフ */}
              <BarGraphButton
                isHover={isHover && hoveringIcon === 'bar'}
                setHoveringIcon={setHoveringIcon}
                isIconDisabled={props.state['error-code'] === ''}
                onClick={e => {
                  e.stopPropagation();
                  props.setGraphParam(
                    'qton',
                    props.state['unit-id'],
                    'bar',
                    props.state['unit-name'],
                    'bar'
                  );
                  props.setSelectedUnit(props.state['unit-id']);
                  navigate(
                    `/pages/my-site/site-detail/${props.siteID}/state/qton/${props.state['unit-id']}`
                  );
                }}
              />
              {/** 温度グラフ */}
              <LineGraphButton
                isHover={isHover && hoveringIcon === 'line'}
                setHoveringIcon={setHoveringIcon}
                isIconDisabled={props.state['error-code'] === ''}
                onClick={e => {
                  e.stopPropagation();
                  props.setGraphParam(
                    'qton',
                    props.state['unit-id'],
                    'line',
                    props.state['unit-name'],
                    'line',
                    childInfo
                  );
                  props.setSelectedUnit(props.state['unit-id']);
                  navigate(
                    `/pages/my-site/site-detail/${props.siteID}/state/qton/${props.state['unit-id']}`
                  );
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </AccordionSummary>
      {/* アコーディオン開き状態 */}
      <AccordionDetails className="subContents__content__body__data__accordionDetails">
        {/* 全体 */}
        <Grid
          container
          className="subContents__content__body__data__accordionDetails"
          onClick={() => {
            props.setGraphParam('');
          }}>
          <Grid item xs={9.5} container alignItems="flex-end">
            {props.state['operation-mode'] === 'heating' ? (
              <HeatingStatus state={props.state} />
            ) : (
              <OtherThanHeating state={props.state} />
            )}
          </Grid>
          {/** グラフ */}
          <Grid item xs={2.5}>
            {/** 消費電力量グラフ */}
            <BarGraphButton
              isHover={isHover && hoveringIcon === 'bar'}
              setHoveringIcon={setHoveringIcon}
              isIconDisabled={props.state['error-code'] === ''}
              onClick={e => {
                e.stopPropagation();
                props.setGraphParam(
                  'qton',
                  props.state['unit-id'],
                  'bar',
                  props.state['unit-name'],
                  'bar'
                );
                props.setSelectedUnit(props.state['unit-id']);
                navigate(
                  `/pages/my-site/site-detail/${props.siteID}/state/qton/${props.state['unit-id']}`
                );
              }}
            />
            {/** 圧力グラフ */}
            <LineGraphButton
              isHover={isHover && hoveringIcon === 'line'}
              setHoveringIcon={setHoveringIcon}
              isIconDisabled={props.state['error-code'] === ''}
              onClick={e => {
                e.stopPropagation();
                props.setGraphParam(
                  'qton',
                  props.state['unit-id'],
                  'line',
                  props.state['unit-name'],
                  'line',
                  childInfo
                );
                props.setSelectedUnit(props.state['unit-id']);
                navigate(
                  `/pages/my-site/site-detail/${props.siteID}/state/qton/${props.state['unit-id']}`
                );
              }}
            />
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
/**
 * Q-ton 子機
 * @param props props
 * - siteID サイトID
 * - state 状態値
 * - selectedUnit 選択状態の空調機
 * - setSelectedUnit 選択状態の空調機のset関数
 * - hoveringUnit ホバー中のユニット
 * - setHoveringUnit ホバー中ユニットのset関数
 * - parentUnitID 親機のユニットID
 * - childUnitInfo 親機登録がされている機器のユニット名・ユニットID・親機のユニットID情報一覧
 * - setUnitTypeFilter フィルタのset関数
 * - setGraphParam グラフのパラメータのset関数
 * @returns JSX
 */
const QtonChildView = (props: {
  siteID: string;
  state: apiIF.resParameters.rtOUStates;
  selectedUnit: string;
  setSelectedUnit: React.Dispatch<React.SetStateAction<string>>;
  hoveringUnit: string;
  setHoveringUnit: React.Dispatch<React.SetStateAction<string>>;
  parentUnitID: string;
  childUnitInfo: apiIF.resParameters.rtUnitInformations[];
  setUnitTypeFilter: React.Dispatch<
    React.SetStateAction<mysite.filterProps['unitType']>
  >;
  setGraphParam: (
    graphTarget: GraphTargetType,
    graphTargetID?: string,
    graphType?: GraphFormType,
    graphTitle?: string,
    tabType?: 'bar' | 'line' | 'diagram',
    childUnitInfo?: apiIF.resParameters.rtUnitInformations[]
  ) => void;
}) => {
  const trans = useTranslation().t; //翻訳関数
  const navigate = useNavigate();

  /** 同じ親機に接続されている子機のID一覧 */
  const childIDs = props.childUnitInfo.reduce((result, item) => {
    if (item['parent-unit'] === props.parentUnitID) {
      result.push(item['unit-id']);
    }
    return result;
  }, [] as string[]);
  /** エラーユニットフラグ エラー有:true エラー無:false */
  const errUnitFlg =
    props.state['error-code'] !== undefined &&
    props.state['error-code'] !== '' && //無課金状態
    props.state['error-code'] !== '00';

  /** 運転/停止アイコン
   * @param runStopState Q-ton子機の運転状態
   */
  const runStopIconSrc = (
    runStopState: apiIF.resParameters.rtOUStates['run-stop']
  ) => {
    switch (runStopState) {
      case 'run':
        return iconWhite.HOT_WATER_ON.src;
      case 'stop':
        return iconBlack.HOT_WATER_OFF.src;
      case 'pause':
        return iconBlack.HOT_WATER_OFF.src;
      default:
        return iconBlack.MISSING.src;
    }
  };
  /** 運転/停止アイコン用クラス名
   * @param runStopState Q-ton子機の運転状態
   */
  const runStopIconClassName = (
    runStopState: apiIF.resParameters.rtOUStates['run-stop']
  ) => {
    switch (runStopState) {
      case 'run':
        return 'subContents__content__body__data__status__runStopIcon--run';
      case 'stop':
        return 'subContents__content__body__data__status__runStopIcon--stop';
      case 'pause':
        return 'subContents__content__body__data__status__runStopIcon--stop';
      default:
        return 'subContents__content__body__data__status__runStopIcon--stop';
    }
  };
  /**
   * 子機ユニット項目のクラス名を決定する関数
   * @param parentID 親機のID
   * @return クラス名
   */
  const childUnitClassName = (parentID: string) => {
    if (
      //親機を選択
      parentID === props.selectedUnit ||
      //親機をホバー
      parentID === props.hoveringUnit ||
      //対象子機、または同じ親機に登録されている子機を選択
      childIDs.includes(props.selectedUnit) ||
      //対象子機、または同じ親機に登録されている子機をホバー
      childIDs.includes(props.hoveringUnit)
    ) {
      return 'subContents__content__body__data subContents__content__body__data--linked';
    }
    return 'subContents__content__body__data';
  };

  /**
   * Q-ton子機が選択状態か判定
   * @param unitID グループID
   */
  const selectHandle = (unitID: string) => {
    if (props.selectedUnit === unitID) {
      props.setSelectedUnit('');
    } else {
      props.setSelectedUnit(unitID);
    }
  };

  return (
    <Grid
      container
      alignItems="center"
      // ユニット選択処理
      onClick={() => {
        selectHandle(props.state['unit-id']);
        //未選択から選択状態になった時のみ実施
        if (props.state['unit-id'] !== props.selectedUnit) {
          props.setGraphParam('');
          props.setSelectedUnit(props.parentUnitID);
          navigate(
            `/pages/my-site/site-detail/${props.siteID}/state/qton/${props.parentUnitID}`
          );
        }
      }}
      //ユニットホバー処理
      onMouseEnter={() => props.setHoveringUnit(props.state['unit-id'])}
      onMouseLeave={() => props.setHoveringUnit('')}
      className={childUnitClassName(props.parentUnitID)}>
      {/* リンクアイコン */}
      <Box className="subContents__content__body__data__linkedIconBorder">
        <img
          src={iconBlack.LINKED.src}
          alt={`${trans(
            `ViewQtonHyozan.body.image.alternativeText.${iconBlack.LINKED.alt}`
          )}`}
          className="subContents__content__body__data__linkedIconBorder__image"
        />
      </Box>
      {/** 空調機名 */}
      <Grid
        item
        xs={gridXsAirUnit}
        title={props.state['unit-name']}
        onClick={() => {
          props.setUnitTypeFilter('qton');
        }}
        className="subContents__content__body__data__category">
        {props.state['unit-name']}
      </Grid>
      {/** 運転/停止、異常表示 */}
      <Grid
        item
        xs={gridXsAirState}
        container
        columnSpacing={2}
        alignItems="center">
        {/** 運転/停止 */}
        <Grid item xs="auto">
          <Box className={runStopIconClassName(props.state['run-stop'])}>
            <img
              src={runStopIconSrc(props.state['run-stop'])}
              alt={`${trans(
                'ViewQtonHyozan.body.image.alternativeText.runStopIcon'
              )}`}
              className="subContents__content__body__data__status__runStopIcon__image"
            />
          </Box>
        </Grid>
        {/** 異常表示 */}
        {/** 異常時のみ表示する */}
        {errUnitFlg && (
          <Grid item xs="auto">
            <img
              src={
                props.state['unit-id'] === props.hoveringUnit
                  ? iconWhite.ERROR.src
                  : iconYellow.ERROR.src
              }
              alt={`${trans(
                'ViewQtonHyozan.body.image.alternativeText.errIcon'
              )}`}
              className="subContents__content__body__data__status__errorIconForUnit"
            />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

/**
 * Qton(暖房モード)のアコーディオン開き状態時に表示する状態値
 * @returns JSX
 */
const HeatingStatus = (props: {state: apiIF.resParameters.rtOUStates}) => {
  const trans = useTranslation().t; //翻訳関数
  return (
    <>
      {/** リモコン設定温度 */}
      <Grid item xs={6}>
        <AccordionDetailsState
          title={trans(
            'ViewQtonHyozan.body.parameter.title.remoteControlPresetTemperature'
          )}
          value={`${trans('ViewQtonHyozan.body.parameter.value.unit.temp', {
            value:
              props.state['remote-control-preset-temperature'] !== undefined &&
              props.state['remote-control-preset-temperature'] !== ''
                ? invertComma(
                    roundOff(
                      props.state['remote-control-preset-temperature'],
                      1
                    )
                  )
                : '--',
          })}`}
        />
      </Grid>
      {/** 熱量 */}
      <Grid item xs={6}>
        <AccordionDetailsState
          title={trans(
            'ViewQtonHyozan.body.parameter.title.energyAmountHeatingMode'
          )}
          value={`${trans(
            'ViewQtonHyozan.body.parameter.value.unit.energyAmount',
            {
              value:
                props.state['energy-amount'] !== undefined &&
                props.state['energy-amount']['heating-mode'] !== undefined &&
                props.state['energy-amount']['heating-mode'] !== ''
                  ? invertComma(
                      roundOff(
                        String(
                          Number(props.state['energy-amount']['heating-mode']) /
                            1000
                        ),
                        1
                      )
                    )
                  : '--',
            }
          )}`}
        />
      </Grid>
    </>
  );
};

/**
 * Qton(暖房以外のモード)のアコーディオン開き状態時に表示する状態値
 * @returns JSX
 */
const OtherThanHeating = (props: {state: apiIF.resParameters.rtOUStates}) => {
  const trans = useTranslation().t; //翻訳関数
  return (
    <>
      {/** 出湯温度 */}
      <Grid item xs={6}>
        <AccordionDetailsState
          title={trans('ViewQtonHyozan.body.parameter.title.outletWaterTemp')}
          value={`${trans('ViewQtonHyozan.body.parameter.value.unit.temp', {
            value:
              props.state['outlet-temperature'] !== undefined &&
              props.state['outlet-temperature'] !== ''
                ? invertComma(roundOff(props.state['outlet-temperature'], 1))
                : '--',
          })}`}
        />
      </Grid>
      {/** 貯湯量 */}
      <Grid item xs={6}>
        <AccordionDetailsState
          title={trans('ViewQtonHyozan.body.parameter.title.hotWaterAmount')}
          value={`${trans('ViewQtonHyozan.body.parameter.value.unit.amount', {
            value:
              props.state['hot-water-amount'] !== undefined &&
              props.state['hot-water-amount'] !== ''
                ? roundOff(props.state['hot-water-amount'])
                : '--',
          })}`}
        />
      </Grid>
    </>
  );
};

/**
 * アコーディオン開き状態で表示する状態値
 * @param props props
 * - title タイトル
 * - value 状態値
 * @returns JSX
 */
const AccordionDetailsState = (props: {
  title: string;
  value: string | React.ReactElement;
}) => {
  return (
    <Stack>
      <Box className="subContents__content__body__data__accordionDetails__title">
        {props.title}
      </Box>
      <Box className="subContents__content__body__data__accordionDetails__value">
        {props.value}
      </Box>
    </Stack>
  );
};

export default ViewOther;
